import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, ClipboardList, Loader, PauseCircle, CheckCircle2, CircleDot } from "lucide-react";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import { InspectionFormDialog } from "@/pages/admin/InspectionFormDialog";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthImage } from "@/components/AuthImage";

const CARDS = [
  { key: "total", label: "إجمالي المعاينات", icon: ClipboardList, cls: "text-slate-900" },
  { key: "assigned", label: "مسندة", icon: CircleDot, cls: "text-slate-600" },
  { key: "in_progress", label: "جاري التنفيذ", icon: Loader, cls: "text-blue-700" },
  { key: "on_hold", label: "معلقة", icon: PauseCircle, cls: "text-amber-600" },
  { key: "completed", label: "مكتملة", icon: CheckCircle2, cls: "text-emerald-700" },
];
const FILTERS = [["", "الكل"], ["assigned", "مسندة"], ["in_progress", "جاري التنفيذ"], ["on_hold", "معلقة"], ["completed", "مكتملة"]];

export default function Dashboard() {
  const nav = useNavigate();
  const { can, user, branches } = useAuth();
  const [stats, setStats] = useState({});
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [branch, setBranch] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const [s, l] = await Promise.all([api.get("/stats"), api.get("/inspections", { params: { status: status || undefined, q: q || undefined, branch: branch || undefined } })]);
    setStats(s.data);
    setItems(l.data);
  }, [status, q, branch]);

  useEffect(() => {
    if (!can("view")) return;
    load().catch(() => {});
    const t = setInterval(() => load().catch(() => {}), 10000);
    return () => clearInterval(t);
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!can("view")) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-8 text-center space-y-2" data-testid="no-view-permission">
        <p className="font-heading text-xl font-bold">مرحباً {user.name}</p>
        <p className="text-slate-500">لا تملك صلاحية عرض المعاينات. تواصل مع المدير الرئيسي.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4 rise">
        <div>
          <p className="text-xs font-bold text-brand-navy mb-1"><span className="font-mono tracking-widest">LIVE</span> · تحديث تلقائي</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">متابعة المعاينات</h1>
        </div>
        {can("manage") && <button onClick={() => setOpen(true)} data-testid="new-inspection-button"
          className="h-12 px-5 rounded-lg bg-brand-red text-white font-bold flex items-center gap-2 hover:bg-brand-red-dark transition-colors">
          <Plus size={18} /> إسناد معاينة جديدة
        </button>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
        {CARDS.map((c, i) => (
          <div key={c.key} className="rise bg-white border border-slate-200 rounded-xl p-4 sm:p-5" style={{ animationDelay: `${i * 60}ms` }}>
            <div className="flex items-center justify-between text-slate-500 text-sm"><span>{c.label}</span><c.icon size={17} className={c.cls} /></div>
            <p className={`font-mono text-3xl font-bold mt-3 ${c.cls}`} data-testid={`stat-${c.key}`}>{stats[c.key] ?? "—"}</p>
          </div>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl">
        <div className="p-4 flex flex-wrap gap-3 items-center border-b border-slate-200">
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map(([v, l]) => (
              <button key={v || "all"} onClick={() => setStatus(v)} data-testid={`filter-${v || "all"}`}
                className={`h-9 px-3 rounded-full text-sm font-semibold border transition-colors ${status === v ? "bg-brand-navy text-white border-brand-navy" : "border-slate-200 hover:bg-slate-100"}`}>{l}</button>
            ))}
          </div>
          <div className="flex gap-1.5 border-s border-slate-200 ps-3">
            {["", ...branches].map((b) => (
              <button key={b || "all"} onClick={() => setBranch(b)} data-testid={`branch-filter-${b || "all"}`}
                className={`h-9 px-3 rounded-full text-sm font-semibold border transition-colors ${branch === b ? "bg-brand-red text-white border-brand-red" : "border-slate-200 hover:bg-slate-100"}`}>{b || "كل الفروع"}</button>
            ))}
          </div>
          <div className="relative ms-auto w-full sm:w-72">
            <Search size={16} className="absolute top-1/2 -translate-y-1/2 start-3 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالاسم، الهاتف، الرقم، المنطقة" className="ps-9 h-10" data-testid="inspections-search-input" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="inspections-table">
            <thead className="text-slate-500 text-xs bg-slate-50">
              <tr>{["الرقم", "العميل", "الفرع / الحي", "المندوب", "موعد الزيارة", "الحالة", "نتيجة المعاينة", "الصور"].map((h) => <th key={h} className="text-start font-semibold px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-slate-500" data-testid="inspections-empty">لا توجد معاينات بعد</td></tr>}
              {items.map((it) => (
                <tr key={it.id} onClick={() => nav(`/admin/inspections/${it.id}`)} data-testid={`inspection-row-${it.id}`}
                  className="border-t border-slate-100 hover:bg-brand-mist cursor-pointer transition-colors">
                  <td className="px-4 py-3 font-mono font-bold">{it.ref}</td>
                  <td className="px-4 py-3"><p className="font-semibold">{it.customer.name}</p><p className="text-xs text-slate-500 font-mono" dir="ltr">{it.customer.phone}</p></td>
                  <td className="px-4 py-3"><p className="font-semibold">{it.customer.branch || "-"}</p><p className="text-xs text-slate-500">{it.customer.area || ""}</p></td>
                  <td className="px-4 py-3">{it.agent_name}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{fmtDate(it.scheduled_at)}</td>
                  <td className="px-4 py-3"><StatusBadge status={it.status} testId={`row-status-${it.id}`} /></td>
                  <td className="px-4 py-3 max-w-[220px]" data-testid={`row-result-${it.id}`}>
                    {it.result?.outcome ? <><p className="font-semibold text-brand-navy">{it.result.outcome}</p>{it.result.notes && <p className="text-xs text-slate-500 truncate">{it.result.notes}</p>}</> : <span className="text-slate-400">{it.hold_reason || "—"}</span>}
                  </td>
                  <td className="px-4 py-3" data-testid={`row-photos-${it.id}`}>
                    <div className="flex items-center gap-1">
                      {it.thumbs?.map((p) => <AuthImage key={p} path={p} className="h-10 w-10 rounded object-cover border border-slate-200" />)}
                      {it.photos_count > 3 && <span className="text-xs text-slate-500 font-mono">+{it.photos_count - 3}</span>}
                      {!it.photos_count && <span className="text-slate-400">—</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <InspectionFormDialog open={open} onClose={() => setOpen(false)} onSaved={() => load()} />
    </div>
  );
}
